import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import {
  Mail,
  Phone,
  Clock,
  AlertCircle,
  CheckCircle2,
  Calendar,
  ExternalLink,
  Copy,
  ChevronDown,
  ChevronRight,
  Tag,
  MessageSquare,
  Send,
  FileText,
  ClipboardList,
  Inbox,
  Play,
  CheckCheck,
  Link2,
  Loader2,
  Upload,
  CircleDot,
  Trophy,
  XCircle,
  Check,
  X,
  Sparkles,
  Eye,
  Download,
  Plus,
  Trash2,
  MoreHorizontal,
  PenLine,
  FileUp,
  ArrowRight,
  CalendarPlus,
  RotateCcw,
  PenSquare,
  History,
  StickyNote,
  Lock,
  AlertTriangle,
} from "lucide-react";

// ============================================================================
// STATUS CONFIGURATIONS
// ============================================================================

const leadStatusConfig = {
  new: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "New", icon: CircleDot },
  contacted: { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Contacted", icon: Mail },
  qualified: { color: "bg-purple-100 text-purple-700 border-purple-200", label: "Qualified", icon: CheckCircle2 },
  won: { color: "bg-green-100 text-green-700 border-green-200", label: "Won", icon: Trophy },
  lost: { color: "bg-gray-100 text-gray-500 border-gray-200", label: "Lost", icon: XCircle }
};

const intakeStatusConfig = {
  intake_requested: { color: "bg-slate-100 text-slate-700", label: "Requested", icon: Send },
  intake_received: { color: "bg-blue-100 text-blue-700", label: "Received", icon: Inbox },
  awaiting_documents: { color: "bg-orange-100 text-orange-700", label: "Awaiting Docs", icon: FileText },
  intake_completed: { color: "bg-emerald-100 text-emerald-700", label: "Completed", icon: CheckCircle2 }
};

const serviceStatusConfig = {
  in_review: { color: "bg-purple-100 text-purple-700", label: "In Review", icon: ClipboardList },
  contract_signed: { color: "bg-teal-100 text-teal-700", label: "Contract Signed", icon: FileText },
  scheduled: { color: "bg-cyan-100 text-cyan-700", label: "Scheduled", icon: Calendar },
  in_service: { color: "bg-indigo-100 text-indigo-700", label: "In Service", icon: Play },
  completed: { color: "bg-green-100 text-green-700", label: "Completed", icon: CheckCheck }
};

const documentStatusConfig = {
  pending: { color: "bg-gray-100 text-gray-600", label: "Pending" },
  uploaded: { color: "bg-green-100 text-green-700", label: "Uploaded" },
  reviewed: { color: "bg-blue-100 text-blue-700", label: "Reviewed" },
};

// File type detection and display helpers
const getFileExtension = (filename) => {
  if (!filename) return "";
  return filename.split(".").pop().toLowerCase();
};

const isPDF = (filename) => getFileExtension(filename) === "pdf";
const isImage = (filename) => ["png", "jpg", "jpeg", "gif", "webp"].includes(getFileExtension(filename));

const getFileTypeBadge = (filename) => {
  const ext = getFileExtension(filename);
  const map = {
    pdf: { label: "PDF", color: "bg-red-50 text-red-600 border-red-200" },
    docx: { label: "DOCX", color: "bg-blue-50 text-blue-600 border-blue-200" },
    doc: { label: "DOC", color: "bg-blue-50 text-blue-600 border-blue-200" },
    txt: { label: "TXT", color: "bg-gray-50 text-gray-600 border-gray-200" },
    png: { label: "PNG", color: "bg-emerald-50 text-emerald-600 border-emerald-200" },
    jpg: { label: "JPG", color: "bg-emerald-50 text-emerald-600 border-emerald-200" },
    jpeg: { label: "JPEG", color: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  };
  return map[ext] || { label: ext.toUpperCase(), color: "bg-gray-50 text-gray-600 border-gray-200" };
};

const normalizeIntakeStatus = (status) => {
  if (status === "client_action_required") return "awaiting_documents";
  return status;
};

// ============================================================================
// SIGNATURE SECTION COMPONENT (kept from original)
// ============================================================================

function SignatureSection({ submission, onRefresh }) {
  const [signwellStatus, setSignwellStatus] = useState(submission?.signwell_status || null);
  const [signingUrl, setSigningUrl] = useState(submission?.signwell_signing_url || null);
  const [sentAt, setSentAt] = useState(submission?.signwell_sent_at || null);
  const [signedAt, setSignedAt] = useState(submission?.signwell_signed_at || null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [signingSettings, setSigningSettings] = useState({ mode: "signwell", signing_link: "" });
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    const fetchSigningSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/business/signing-settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data) {
          setSigningSettings({
            mode: response.data.mode || "signwell",
            signing_link: response.data.signing_link || "",
            signwell_connected: response.data.signwell_connected || false
          });
        }
      } catch (err) {
        console.error("Failed to fetch signing settings:", err);
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSigningSettings();
  }, []);

  useEffect(() => {
    if (submission?.signwell_document_id) {
      setSignwellStatus(submission?.signwell_status || null);
      setSigningUrl(submission?.signwell_signing_url || null);
      setSentAt(submission?.signwell_sent_at || null);
      setSignedAt(submission?.signwell_signed_at || null);
    }
  }, [submission]);

  const handleUploadAndSend = async () => {
    if (!selectedFile) return;
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await axios.post(
        `${API}/submissions/${submission.id}/send-for-signature`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      if (response.data.status === "success") {
        toast.success("Document sent for signature!");
        setSignwellStatus("pending");
        setSigningUrl(response.data.signing_url);
        setSentAt(new Date().toISOString());
        setShowUploadModal(false);
        setSelectedFile(null);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send document");
    } finally {
      setSending(false);
    }
  };

  if (loadingSettings) return null;

  // External signing link mode
  if (signingSettings.mode === "link" && signingSettings.signing_link) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start text-left"
        onClick={() => window.open(signingSettings.signing_link, "_blank")}
      >
        <ExternalLink className="w-4 h-4 mr-2" />
        Open Signing Portal
      </Button>
    );
  }

  // SignWell mode
  return (
    <div className="space-y-2">
      {signwellStatus === "signed" ? (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <CheckCircle2 className="w-4 h-4" />
          Document signed
        </div>
      ) : signwellStatus === "pending" ? (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <Clock className="w-4 h-4" />
          Awaiting signature
        </div>
      ) : (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowUploadModal(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
          <Button variant="outline" size="sm" className="flex-1" disabled>
            <Send className="w-4 h-4 mr-2" />
            Send for Signature
          </Button>
        </div>
      )}

      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document for Signature</DialogTitle>
            <DialogDescription>Upload a PDF document to send for e-signature.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input type="file" accept=".pdf" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
            <Button onClick={handleUploadAndSend} disabled={!selectedFile || sending}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send for Signature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// MAIN SUBMISSION DRAWER COMPONENT
// ============================================================================

export default function SubmissionDrawer({
  open,
  onClose,
  submission,
  token,
  onStatusChange,
  onUpdateSubmission,
  emailTemplates,
  intakeForms,
  leadForms,
  onSendIntake,
  onSendEmail,
  onRefresh,
  onRequestDocuments,
  onSendForSignature
}) {
  // =========================================================================
  // STATE
  // =========================================================================
  
  // Collapsible sections
  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(false);
  const [formsExpanded, setFormsExpanded] = useState({});
  
  // Documents
  const [addDocModalOpen, setAddDocModalOpen] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState("");
  const [newDocRequired, setNewDocRequired] = useState(true);
  const [savingDoc, setSavingDoc] = useState(false);
  const [updatingDocStatus, setUpdatingDocStatus] = useState(null);
  
  // Notes
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  
  // Tags
  const [newTag, setNewTag] = useState("");
  const [tagInputOpen, setTagInputOpen] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  
  // Right sidebar tab state
  const [activeRightTab, setActiveRightTab] = useState("activity");
  
  // Links
  const [externalUploadLink, setExternalUploadLink] = useState("");
  const [savingExternalLink, setSavingExternalLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(null);
  
  // Email status
  const [emailStatus, setEmailStatus] = useState(null);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendingEmail, setResendingEmail] = useState(false);
  
  // Modals
  const [wonModalOpen, setWonModalOpen] = useState(false);
  const [wonModalData, setWonModalData] = useState({ sendIntakeEmail: true, intakeFormId: null });
  const [wonModalLoading, setWonModalLoading] = useState(false);
  const [leadFormModalOpen, setLeadFormModalOpen] = useState(false);
  const [leadFormData, setLeadFormData] = useState({ formId: null });
  const [leadFormLoading, setLeadFormLoading] = useState(false);
  const [composeEmailOpen, setComposeEmailOpen] = useState(false);
  const [templateSelectOpen, setTemplateSelectOpen] = useState(false);
  
  // E-Signature file upload
  const [esignUploadOpen, setEsignUploadOpen] = useState(false);
  const [esignFile, setEsignFile] = useState(null);
  const [uploadingEsign, setUploadingEsign] = useState(false);

  // Workflow transition state
  const [pendingTransition, setPendingTransition] = useState(null);
  const [showBackwardConfirm, setShowBackwardConfirm] = useState(false);

  // Lead → Intake transition modal (when Won is selected)
  const [showWonIntakeModal, setShowWonIntakeModal] = useState(false);
  const [wonIntakeLoading, setWonIntakeLoading] = useState(false);

  // =========================================================================
  // EFFECTS
  // =========================================================================

  // Fetch scheduling link from workflow settings (where Settings page saves it)
  const [schedulingLink, setSchedulingLink] = useState(null);
  
  useEffect(() => {
    const fetchSchedulingLink = async () => {
      try {
        // Settings page saves to /workflow/settings, not /business/scheduling-links
        const response = await axios.get(`${API}/workflow/settings`, { headers: { Authorization: `Bearer ${token}` } });
        const settings = response.data;
        // scheduling_link is Calendly, calendar_link is Google Calendar
        const link = settings.scheduling_link || settings.calendar_link || null;
        setSchedulingLink(link);
      } catch (err) {
        console.error("Failed to fetch scheduling link:", err);
      }
    };
    if (token) fetchSchedulingLink();
  }, [token]);

  useEffect(() => {
    if (submission) {
      setExternalUploadLink(submission.external_upload_link || "");
    }
  }, [submission?.id]);

  useEffect(() => {
    const fetchEmailStatus = async () => {
      if (!submission?.id) return;
      try {
        const response = await axios.get(`${API}/submissions/${submission.id}/email-status`, { headers: { Authorization: `Bearer ${token}` } });
        setEmailStatus(response.data);
      } catch (err) {
        console.error("Failed to fetch email status:", err);
      }
    };
    fetchEmailStatus();
  }, [submission?.id, token]);

  // =========================================================================
  // GUARD
  // =========================================================================

  if (!submission || !submission.id) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-2xl p-0">
          <div className="p-6"><SheetTitle>Loading...</SheetTitle></div>
        </SheetContent>
      </Sheet>
    );
  }

  // =========================================================================
  // HELPERS
  // =========================================================================

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const getName = () => {
    const fd = submission.form_data || {};
    return submission.customer_name || fd.name || fd.full_name || fd.client_name || "Unknown";
  };

  const getEmail = () => submission.customer_email || submission.form_data?.email || "";
  const getPhone = () => submission.customer_phone || submission.form_data?.phone || "";
  
  const getClientPortalLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/client/${submission.id}`;
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setLinkCopied(type);
    toast.success("Copied to clipboard");
    setTimeout(() => setLinkCopied(null), 2000);
  };

  // Open document with file-type-aware behavior
  // PDF → opens in viewer, non-PDF → downloads
  // Generates fresh signed URL on-demand to avoid expiry issues
  const openDocument = async (file) => {
    const url = file.file_url || file.url;
    const storagePath = file.storage_path;
    const filename = file.filename || file.file_name || "";
    
    // If we have a storage_path, get a fresh signed URL
    let targetUrl = url;
    if (storagePath && token) {
      try {
        const resp = await axios.post(`${API}/documents/signed-url`, 
          { storage_path: storagePath },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        targetUrl = resp.data.url;
      } catch (e) {
        // Fall back to stored URL
        console.warn("Failed to get fresh signed URL, using stored URL");
      }
    }
    
    if (!targetUrl) {
      toast.error("No download URL available");
      return;
    }
    
    // Open in new tab — browser handles PDF inline, downloads everything else
    window.open(targetUrl, "_blank");
  };

  // Computed values
  const leadStatus = submission.lead_status || submission.status || "new";
  const intakeStatus = normalizeIntakeStatus(submission.intake_stage || submission.intake_status);
  const serviceStatus = submission.service_stage;
  const requiredDocuments = submission.required_documents || [];
  const aiScore = submission.ai_score || submission.lead_score;
  const priorityLabel = submission.priority_label || submission.lead_score_level;
  const isLeadForm = submission.mode !== "post_booking";
  const isIntakePhase = leadStatus === "won" || submission.mode === "post_booking";

  // =========================================================================
  // TERMINAL STATE CHECK - "Lost" is a terminal state that locks workflow
  // =========================================================================
  const isTerminalLost = leadStatus === "lost";

  // =========================================================================
  // ACTIVE PHASE LOGIC - ONLY ONE PHASE CAN BE ACTIVE
  // =========================================================================
  // Determine which phase group is currently ACTIVE
  // TERMINAL: If Lead = Lost, no phase is active (workflow is terminated)
  // Priority: Service > Intake > Lead
  const getActivePhase = () => {
    // TERMINAL: Lost leads have no active phase - workflow is closed
    if (isTerminalLost) {
      return "lost";
    }
    // If service_stage is set (not null/undefined), Service phase is active
    if (serviceStatus && serviceStatus !== "") {
      return "service";
    }
    // If lead is "won" and intake has started, Intake phase is active
    if (leadStatus === "won" && intakeStatus) {
      return "intake";
    }
    // Otherwise Lead phase is active
    return "lead";
  };
  
  const activePhase = getActivePhase();
  
  // Get the current value for the dropdown based on active phase
  const getCurrentPhaseValue = () => {
    if (activePhase === "service") return serviceStatus;
    if (activePhase === "intake") return intakeStatus;
    return leadStatus;
  };

  // Determine phase completion status for workflow stepper
  const isLeadComplete = leadStatus === "won" || leadStatus === "lost" || activePhase === "intake" || activePhase === "service";
  const isIntakeComplete = intakeStatus === "intake_completed" || activePhase === "service";
  const isServiceActive = activePhase === "service";

  // Compute "Next Step" message and action based on active phase
  const getNextStep = () => {
    if (activePhase === "lead") {
      if (leadStatus === "new") return { message: "Follow up with lead", action: "compose", show: true };
      if (leadStatus === "contacted") return { message: "Qualify this lead", action: "status", show: true };
      if (leadStatus === "qualified") return { message: "Close the deal", action: "won", show: true };
      return { message: null, action: null, show: false };
    }
    if (activePhase === "intake") {
      if (intakeStatus === "intake_requested") return { message: "Awaiting client response", action: null, show: true };
      if (intakeStatus === "intake_received") return { message: "Review intake form", action: "forms", show: true };
      if (intakeStatus === "awaiting_documents") return { message: "Request documents", action: "documents", show: true };
      if (!intakeStatus) return { message: "Send intake form", action: "send", show: true };
      return { message: null, action: null, show: false };
    }
    if (activePhase === "service") {
      if (serviceStatus === "in_review") return { message: "Review client case", action: null, show: true };
      if (serviceStatus === "contract_signed") return { message: "Schedule service", action: null, show: true };
      if (serviceStatus === "scheduled") return { message: "Begin service", action: null, show: true };
      if (serviceStatus === "in_service") return { message: "Complete service", action: null, show: true };
      return { message: null, action: null, show: false };
    }
    return { message: null, action: null, show: false };
  };

  const nextStep = getNextStep();

  // Handle Next Step click action
  const handleNextStepClick = () => {
    const action = nextStep.action;
    if (action === "compose") {
      const to = getEmail();
      if (!to) { toast.error("No email address on record for this contact"); return; }
      window.location.href = `mailto:${encodeURIComponent(to)}`;
    }
    else if (action === "send") onSendIntake?.(submission);
    else if (action === "won") handleWonClick();
    else if (action === "forms") {
      document.querySelector('[data-testid="forms-responses-section"]')?.scrollIntoView({ behavior: 'smooth' });
    }
    else if (action === "documents") {
      document.querySelector('[data-testid="documents-section"]')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleWonClick = () => {
    setWonModalData({ sendIntakeEmail: true, intakeFormId: intakeForms?.[0]?.id || null });
    setWonModalOpen(true);
  };

  const handleConfirmWon = async () => {
    setWonModalLoading(true);
    try {
      const response = await axios.post(`${API}/leads/${submission.id}/mark-won`, {
        send_intake_email: wonModalData.sendIntakeEmail,
        intake_form_id: wonModalData.intakeFormId
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (response.data.status === "success") {
        toast.success("Lead marked as Won!");
        if (response.data.email_sent) toast.success("Intake email sent");
        if (onRefresh) onRefresh();
      }
      setWonModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as won");
    } finally {
      setWonModalLoading(false);
    }
  };

  const handleIntakeStageChange = async (newStage) => {
    // Use unified workflow transition
    await handleUnifiedWorkflowTransition(newStage);
  };

  const handleServiceStageChange = async (newStage) => {
    // Use unified workflow transition
    await handleUnifiedWorkflowTransition(newStage);
  };

  // =========================================================================
  // UNIFIED WORKFLOW TRANSITION HANDLER
  // =========================================================================
  const handleUnifiedWorkflowTransition = async (targetStatus, confirmBackward = false) => {
    try {
      const response = await axios.put(
        `${API}/leads/${submission.id}/workflow-transition`,
        { target_status: targetStatus, confirm_backward: confirmBackward },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "requires_confirmation") {
        // Show confirmation dialog for backward movement
        setPendingTransition({ targetStatus, currentPhase: response.data.current_phase, targetPhase: response.data.target_phase });
        setShowBackwardConfirm(true);
        return;
      }

      if (response.data.status === "success") {
        // Show appropriate toast based on what happened
        const { is_backward_movement, stages_reset, active_phase } = response.data;
        
        if (is_backward_movement && stages_reset && stages_reset.length > 0) {
          // Show reset notification
          const resetNames = stages_reset.map(s => s.replace("_stage", "").charAt(0).toUpperCase() + s.replace("_stage", "").slice(1)).join(", ");
          toast.info(`${resetNames} stage has been reset due to workflow change`);
        }
        
        toast.success(`Status updated to ${targetStatus.replace(/_/g, " ")}`);
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update workflow");
    }
  };

  const confirmBackwardTransition = async () => {
    if (pendingTransition) {
      setShowBackwardConfirm(false);
      await handleUnifiedWorkflowTransition(pendingTransition.targetStatus, true);
      setPendingTransition(null);
    }
  };

  const cancelBackwardTransition = () => {
    setShowBackwardConfirm(false);
    setPendingTransition(null);
  };

  // =========================================================================
  // WON → INTAKE TRANSITION HANDLERS
  // =========================================================================
  
  /**
   * Handle "Start Intake & Send Form" action
   * 1. Mark lead as Won
   * 2. Move to Intake phase (requested)
   * 3. Send intake form email
   */
  const handleWonStartIntakeWithEmail = async () => {
    setWonIntakeLoading(true);
    try {
      // First mark as Won (this may auto-transition if configured)
      await axios.put(
        `${API}/leads/${submission.id}/workflow-transition`,
        { target_status: "won", confirm_backward: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Then transition to intake_requested and send the intake form email
      const response = await axios.post(
        `${API}/leads/${submission.id}/mark-won`,
        { send_intake_form: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "success") {
        if (response.data.email_sent) {
          toast.success("Lead marked as Won, intake started, and form sent!");
        } else {
          toast.success("Lead marked as Won and intake started");
          if (response.data.email_error) {
            toast.warning(`Email not sent: ${response.data.email_error}`);
          }
        }
        setShowWonIntakeModal(false);
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start intake process");
    } finally {
      setWonIntakeLoading(false);
    }
  };

  /**
   * Handle "Start Intake Only" action
   * 1. Mark lead as Won
   * 2. Move to Intake phase (requested)
   * 3. Do NOT send email
   */
  const handleWonStartIntakeOnly = async () => {
    setWonIntakeLoading(true);
    try {
      // First mark as Won
      await axios.put(
        `${API}/leads/${submission.id}/workflow-transition`,
        { target_status: "won", confirm_backward: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Then transition to intake_requested WITHOUT sending email
      const response = await axios.post(
        `${API}/leads/${submission.id}/mark-won`,
        { send_intake_form: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "success") {
        toast.success("Lead marked as Won and intake started");
        setShowWonIntakeModal(false);
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start intake process");
    } finally {
      setWonIntakeLoading(false);
    }
  };

  /**
   * Handle "Skip" action
   * 1. Mark lead as Won only
   * 2. Stay in Lead phase
   */
  const handleWonSkipIntake = async () => {
    setWonIntakeLoading(true);
    try {
      // Just mark as Won, don't auto-transition to intake
      const response = await axios.put(
        `${API}/leads/${submission.id}/workflow-transition`,
        { target_status: "won", confirm_backward: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "success") {
        toast.success("Lead marked as Won");
        setShowWonIntakeModal(false);
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update lead status");
    } finally {
      setWonIntakeLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!submission?.id) return;
    setResendingEmail(true);
    try {
      const response = await axios.post(`${API}/submissions/${submission.id}/resend-email`, { email: resendEmail || undefined }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.status === "success") {
        toast.success("Email sent successfully");
        setResendModalOpen(false);
        setResendEmail("");
        if (onRefresh) onRefresh();
      } else {
        toast.error(response.data.error || "Failed to send");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setResendingEmail(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/leads/${submission.id}/notes`, { content: newNote }, { headers: { Authorization: `Bearer ${token}` } });
      setNewNote("");
      toast.success("Note added");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleAddDocument = async () => {
    if (!newDocLabel.trim()) return;
    setSavingDoc(true);
    try {
      await axios.post(`${API}/submissions/${submission.id}/documents`, { label: newDocLabel, required: newDocRequired }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Document added");
      setAddDocModalOpen(false);
      setNewDocLabel("");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to add document");
    } finally {
      setSavingDoc(false);
    }
  };

  const handleDocumentStatusChange = async (docId, newStatus) => {
    setUpdatingDocStatus(docId);
    try {
      await axios.patch(`${API}/submissions/${submission.id}/documents/${docId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Document status updated");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to update");
    } finally {
      setUpdatingDocStatus(null);
    }
  };

  const handleSaveExternalLink = async () => {
    setSavingExternalLink(true);
    try {
      await axios.patch(`${API}/leads/${submission.id}`, { external_upload_link: externalUploadLink }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Link saved");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSavingExternalLink(false);
    }
  };

  // Handle adding a new tag
  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    setSavingTag(true);
    try {
      const currentTags = submission.tags || [];
      const updatedTags = [...currentTags, newTag.trim()];
      // Use the dedicated tags endpoint
      await axios.put(`${API}/leads/${submission.id}/tags`, { tags: updatedTags }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tag added");
      setNewTag("");
      setTagInputOpen(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to add tag");
    } finally {
      setSavingTag(false);
    }
  };

  // Handle removing a tag
  const handleRemoveTag = async (tagToRemove) => {
    try {
      const currentTags = submission.tags || [];
      const updatedTags = currentTags.filter(t => t !== tagToRemove);
      // Use the dedicated tags endpoint
      await axios.put(`${API}/leads/${submission.id}/tags`, { tags: updatedTags }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tag removed");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to remove tag");
    }
  };

  // Get forms data
  // Form responses - Always include lead form data if it exists
  const leadFormResponses = submission.lead_form_responses || 
    (submission.form_data && Object.keys(submission.form_data).length > 0 ? 
      [{ form_name: submission.form_name || "Lead Form", data: submission.form_data, submitted_at: submission.created_at }] : 
      []);
  const intakeFormResponses = submission.intake_form_responses || 
    (submission.intake_form_data && Object.keys(submission.intake_form_data).length > 0 ? 
      [{ form_name: submission.intake_form_name || "Intake Form", data: submission.intake_form_data, submitted_at: submission.intake_submitted_at }] : 
      []);

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-4xl p-0 flex flex-col" data-testid="submission-drawer">
        
        {/* ================================================================= */}
        {/* HEADER - Simplified 2-row structure */}
        {/* ================================================================= */}
        <div className="px-5 py-4 border-b bg-white flex-shrink-0">
          {/* Row 1: Name + Status */}
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <h2 className="text-lg font-semibold truncate" data-testid="drawer-client-name">{getName()}</h2>
              {/* BADGE PRIORITY: Lost (terminal) > Service > Intake > Lead */}
              {isTerminalLost ? (
                <Badge className="bg-gray-100 text-gray-600 border-gray-300 text-xs font-medium" data-testid="drawer-status-badge">
                  Lost
                </Badge>
              ) : serviceStatus && serviceStatusConfig[serviceStatus] ? (
                <Badge className={`${serviceStatusConfig[serviceStatus].color} text-xs font-medium`} data-testid="drawer-status-badge">
                  {serviceStatusConfig[serviceStatus].label}
                </Badge>
              ) : intakeStatus && intakeStatusConfig[intakeStatus] ? (
                <Badge className={`${intakeStatusConfig[intakeStatus].color} text-xs font-medium`} data-testid="drawer-status-badge">
                  {intakeStatusConfig[intakeStatus].label}
                </Badge>
              ) : leadStatusConfig[leadStatus] && (
                <Badge className={`${leadStatusConfig[leadStatus].color} text-xs font-medium`} data-testid="drawer-status-badge">
                  {leadStatusConfig[leadStatus].label}
                </Badge>
              )}
              {isLeadForm && aiScore && !isTerminalLost && (
                <Badge 
                  variant="outline" 
                  className={`text-xs font-medium ${submission.ai_analysis_used === false ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-violet-50 text-violet-700 border-violet-200'}`}
                  data-testid="ai-score-badge"
                >
                  Score: {aiScore}{submission.ai_analysis_used === false ? " (rule-based)" : ""}
                </Badge>
              )}
            </div>
            
            {/* Right: Primary CTA + Overflow */}
            <div className="flex items-center gap-2">
              {leadStatus !== "won" && leadStatus !== "lost" && isLeadForm ? (
                <Button size="sm" onClick={handleWonClick} className="bg-green-600 hover:bg-green-700 h-8" data-testid="mark-won-btn">
                  <Trophy className="w-3.5 h-3.5 mr-1.5" />
                  Mark Won
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="h-8" onClick={() => {
                  // Use scheduling link fetched from business settings
                  if (schedulingLink) {
                    window.open(schedulingLink, "_blank");
                  } else {
                    toast.info("No scheduling link configured. Go to Settings → Integrations to set up Calendly.");
                  }
                }} data-testid="schedule-call-btn">
                  <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
                  Schedule Call
                </Button>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="more-actions-btn">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => window.open(getClientPortalLink(), "_blank")}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Client Portal
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => copyToClipboard(getClientPortalLink(), "portal")}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Portal Link
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {leadStatus !== "lost" && isLeadForm && (
                    <DropdownMenuItem onClick={() => onStatusChange?.(submission.id, "lost")} className="text-red-600">
                      <XCircle className="w-4 h-4 mr-2" />
                      Mark as Lost
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} data-testid="close-drawer-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Row 2: Contact info with clear date label */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {getEmail() && (
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {getEmail()}
              </span>
            )}
            {getPhone() && (
              <>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {getPhone()}
                </span>
              </>
            )}
            <span className="text-slate-300">•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>Created {formatDate(submission.created_at)}</span>
            </span>
          </div>
        </div>

        {/* ================================================================= */}
        {/* MAIN CONTENT - Two column layout (70/30) */}
        {/* ================================================================= */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* LEFT COLUMN - Main content (~70%) */}
          <ScrollArea className="flex-1 border-r">
            <div className="p-5 space-y-5">
              
              {/* Email Delivery Warning */}
              {emailStatus?.has_issues && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-red-800">
                      {emailStatus.has_bounced ? "Email bounced" : "Email failed"}
                    </p>
                    <Button variant="link" size="sm" className="h-auto p-0 text-red-700 text-xs" onClick={() => { setResendEmail(getEmail()); setResendModalOpen(true); }}>
                      Edit & resend
                    </Button>
                  </div>
                </div>
              )}

              {/* ============================================================= */}
              {/* QUICK ACTIONS - Compact inline buttons */}
              {/* ============================================================= */}
              <div className="flex items-center gap-2" data-testid="actions-section">
                <span className="text-xs text-muted-foreground font-medium">Quick Actions:</span>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => onSendIntake?.(submission)} data-testid="resend-intake-btn">
                  <RotateCcw className="w-3 h-3 mr-1.5" />
                  {isIntakePhase ? "Resend Form" : "Send Form"}
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => {
                  const to = getEmail();
                  if (!to) { toast.error("No email address on record for this contact"); return; }
                  // Compose Email hands off to the user's own email client
                  // (Outlook, Gmail, Apple Mail, whatever's configured).
                  // No backend/Resend routing, so no sender-domain setup
                  // and no template variables to render — the user writes,
                  // reviews, and sends from their own account.
                  window.location.href = `mailto:${encodeURIComponent(to)}`;
                }} data-testid="compose-email-btn">
                  <PenSquare className="w-3 h-3 mr-1.5" />
                  Compose Email
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setTemplateSelectOpen(true)} data-testid="use-template-btn">
                  <FileText className="w-3 h-3 mr-1.5" />
                  Use Template
                </Button>
              </div>

              {/* ============================================================= */}
              {/* WORKFLOW - Visual stepper + Grouped dropdown */}
              {/* ============================================================= */}
              <div data-testid="workflow-section">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Workflow</h3>
                
                {/* TERMINAL STATE: Lost - Show simplified closed workflow */}
                {isTerminalLost ? (
                  <div className="mb-4">
                    <div className="flex items-center gap-1">
                      {/* Lead Phase - LOST (terminal) */}
                      <div className="flex-1 px-3 py-2 rounded-l-md border bg-gray-100 border-gray-300">
                        <div className="flex items-center gap-1.5">
                          <XCircle className="w-3.5 h-3.5 text-gray-500" />
                          <span className="text-xs font-medium text-gray-600">Lead</span>
                        </div>
                        <p className="text-[10px] mt-0.5 text-gray-500">Lost</p>
                      </div>
                      
                      <ChevronRight className="w-4 h-4 text-gray-300 -mx-1 relative z-10" />
                      
                      {/* Intake Phase - LOCKED */}
                      <div className="flex-1 px-3 py-2 border bg-gray-50 border-gray-200 opacity-50">
                        <div className="flex items-center gap-1.5">
                          <Lock className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-xs font-medium text-gray-400">Intake</span>
                        </div>
                        <p className="text-[10px] mt-0.5 text-gray-400">Closed</p>
                      </div>
                      
                      <ChevronRight className="w-4 h-4 text-gray-300 -mx-1 relative z-10" />
                      
                      {/* Service Phase - LOCKED */}
                      <div className="flex-1 px-3 py-2 rounded-r-md border bg-gray-50 border-gray-200 opacity-50">
                        <div className="flex items-center gap-1.5">
                          <Lock className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-xs font-medium text-gray-400">Service</span>
                        </div>
                        <p className="text-[10px] mt-0.5 text-gray-400">Closed</p>
                      </div>
                    </div>
                    
                    {/* Reactivation dropdown for Lost leads */}
                    <div className="mt-3 space-y-2">
                      <div className="p-3 bg-amber-50 rounded-md border border-amber-200">
                        <p className="text-xs text-amber-700 mb-2">
                          <span className="font-semibold">Workflow closed.</span> Select a status below to reactivate.
                        </p>
                        <Select 
                          value="lost"
                          onValueChange={(val) => {
                            if (val !== "lost") {
                              // Handle reactivation through unified transition
                              handleUnifiedWorkflowTransition(val);
                            }
                          }}
                        >
                          <SelectTrigger className="w-full h-9 bg-white">
                            <SelectValue placeholder="Reactivate workflow..." />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Lead Phase Group */}
                            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-slate-50">Lead Phase (Reactivate)</div>
                            {Object.entries(leadStatusConfig).filter(([key]) => key !== "lost").map(([key, config]) => (
                              <SelectItem key={key} value={key} className="pl-4">{config.label}</SelectItem>
                            ))}
                            
                            {/* Intake Phase Group */}
                            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-slate-50 mt-1">Intake Phase</div>
                            {Object.entries(intakeStatusConfig).map(([key, config]) => (
                              <SelectItem key={key} value={key} className="pl-4">{config.label}</SelectItem>
                            ))}
                            
                            {/* Service Phase Group */}
                            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-slate-50 mt-1">Service Phase</div>
                            {Object.entries(serviceStatusConfig).map(([key, config]) => (
                              <SelectItem key={key} value={key} className="pl-4">{config.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ACTIVE WORKFLOW: Normal stepper */
                  <div className="flex items-center gap-1 mb-4">
                    {/* Lead Phase */}
                    <div className={`flex-1 px-3 py-2 rounded-l-md border-y border-l ${
                      activePhase === "lead" 
                        ? "bg-blue-50 border-blue-200" 
                        : isLeadComplete 
                          ? "bg-green-50 border-green-200" 
                          : "bg-slate-50 border-slate-200"
                    }`}>
                      <div className="flex items-center gap-1.5">
                        {isLeadComplete && activePhase !== "lead" ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        ) : activePhase === "lead" ? (
                          <CircleDot className="w-3.5 h-3.5 text-blue-600" />
                        ) : (
                          <CircleDot className="w-3.5 h-3.5 text-slate-400" />
                        )}
                        <span className="text-xs font-medium">Lead</span>
                      </div>
                      <p className={`text-[10px] mt-0.5 ${
                        isLeadComplete && activePhase !== "lead" 
                          ? "text-green-600" 
                          : activePhase === "lead" 
                            ? "text-blue-600" 
                            : "text-slate-400"
                      }`}>
                        {isLeadComplete && activePhase !== "lead" 
                          ? "Won" 
                          : leadStatusConfig[leadStatus]?.label || "New"}
                      </p>
                    </div>
                    
                    <ChevronRight className="w-4 h-4 text-slate-300 -mx-1 relative z-10" />
                    
                    {/* Intake Phase */}
                    <div className={`flex-1 px-3 py-2 border-y ${
                      activePhase === "intake" 
                        ? "bg-blue-50 border-blue-200" 
                        : isIntakeComplete 
                          ? "bg-green-50 border-green-200" 
                          : "bg-slate-50 border-slate-200"
                    }`}>
                      <div className="flex items-center gap-1.5">
                        {isIntakeComplete && activePhase !== "intake" ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        ) : activePhase === "intake" ? (
                          <CircleDot className="w-3.5 h-3.5 text-blue-600" />
                        ) : (
                          <CircleDot className="w-3.5 h-3.5 text-slate-400" />
                        )}
                        <span className="text-xs font-medium">Intake</span>
                      </div>
                      <p className={`text-[10px] mt-0.5 ${
                        isIntakeComplete && activePhase !== "intake" 
                          ? "text-green-600" 
                          : activePhase === "intake" 
                            ? "text-blue-600" 
                            : "text-slate-400"
                      }`}>
                        {isIntakeComplete && activePhase !== "intake" 
                          ? "Completed" 
                          : activePhase === "intake" && intakeStatus 
                            ? intakeStatusConfig[intakeStatus]?.label 
                            : "Not Started"}
                      </p>
                    </div>
                    
                    <ChevronRight className="w-4 h-4 text-slate-300 -mx-1 relative z-10" />
                    
                    {/* Service Phase */}
                    <div className={`flex-1 px-3 py-2 rounded-r-md border-y border-r ${
                      activePhase === "service" 
                        ? (serviceStatus === "completed" ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200")
                        : "bg-slate-50 border-slate-200"
                    }`}>
                      <div className="flex items-center gap-1.5">
                        {serviceStatus === "completed" ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                        ) : activePhase === "service" ? (
                          <CircleDot className="w-3.5 h-3.5 text-blue-600" />
                        ) : (
                          <CircleDot className="w-3.5 h-3.5 text-slate-400" />
                        )}
                        <span className={`text-xs font-medium ${activePhase !== "service" ? "text-slate-500" : ""}`}>Service</span>
                      </div>
                      <p className={`text-[10px] mt-0.5 ${
                        serviceStatus === "completed" 
                          ? "text-green-600" 
                          : activePhase === "service" 
                            ? "text-blue-600" 
                            : "text-slate-400"
                      }`}>
                        {serviceStatus === "completed" 
                          ? "Completed" 
                          : activePhase === "service" && serviceStatus 
                            ? serviceStatusConfig[serviceStatus]?.label 
                            : "Not Started"}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Status Dropdown - Grouped with helper text */}
                {!isTerminalLost ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Current phase: <span className="font-medium capitalize">{activePhase}</span></p>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Update phase:</span>
                    <Select 
                      value={getCurrentPhaseValue()} 
                      onValueChange={(val) => {
                        // SPECIAL CASE: "Won" triggers the Intake transition modal
                        if (val === "won" && leadStatus !== "won") {
                          setShowWonIntakeModal(true);
                          return;
                        }
                        
                        // SPECIAL CASE: "Lost" must go through the parent so the
                        // Lost Reason modal opens before the status is persisted.
                        if (val === "lost" && leadStatus !== "lost") {
                          onStatusChange?.(submission.id, "lost");
                          return;
                        }
                        
                        // Use unified workflow transition for ALL status changes
                        // This handles forward progression, backward movement, and cascade resets
                        if (Object.keys(leadStatusConfig).includes(val)) {
                          // Lead status change - use unified transition for proper state management
                          handleUnifiedWorkflowTransition(val);
                        } else if (Object.keys(intakeStatusConfig).includes(val)) {
                          handleIntakeStageChange(val);
                        } else if (Object.keys(serviceStatusConfig).includes(val)) {
                          handleServiceStageChange(val);
                        }
                      }}
                    >
                      <SelectTrigger className="w-48 h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Lead Phase Group */}
                        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-slate-50">Lead Phase</div>
                        {Object.entries(leadStatusConfig).map(([key, config]) => (
                          <SelectItem key={key} value={key} className="pl-4">{config.label}</SelectItem>
                        ))}
                        
                        <DropdownMenuSeparator />
                        
                        {/* Intake Phase Group */}
                        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-slate-50">Intake Phase</div>
                        {Object.entries(intakeStatusConfig).map(([key, config]) => (
                          <SelectItem key={key} value={key} className="pl-4">{config.label}</SelectItem>
                        ))}
                        
                        <DropdownMenuSeparator />
                        
                        {/* Service Phase Group */}
                        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-slate-50">Service Phase</div>
                        {Object.entries(serviceStatusConfig).map(([key, config]) => (
                          <SelectItem key={key} value={key} className="pl-4">{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                ) : null}
              </div>

              {/* ============================================================= */}
              {/* NEXT STEP - Actionable (hide if no action) */}
              {/* ============================================================= */}
              {nextStep.show && nextStep.message && (
                <div 
                  className={`bg-slate-50 border border-slate-200 rounded-md p-3 ${nextStep.action ? "cursor-pointer hover:bg-slate-100 transition-colors" : ""}`}
                  data-testid="next-step-section"
                  onClick={nextStep.action ? handleNextStepClick : undefined}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
                        <ArrowRight className="w-3 h-3 text-violet-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Next Step</p>
                        <p className="text-sm font-medium">{nextStep.message}</p>
                      </div>
                    </div>
                    {nextStep.action && (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              )}

              {/* ============================================================= */}
              {/* DOCUMENTS - Clean list with actions */}
              {/* ============================================================= */}
              <div data-testid="documents-section">
                {/* Lead Form Documents (Submitted with Lead) */}
                {(submission.lead_files || []).length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Submitted with Lead</h3>
                    <div className="space-y-1.5">
                      {submission.lead_files.map((lf, idx) => {
                        const ext = getFileExtension(lf.filename);
                        const typeBadge = getFileTypeBadge(lf.filename);
                        const isPdf = isPDF(lf.filename);
                        return (
                          <div key={lf.file_id || idx} className="flex items-center justify-between py-2 px-3 bg-blue-50/50 rounded-md text-sm">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <span className="font-medium truncate block">{lf.filename}</span>
                                {lf.field_label && <span className="text-xs text-muted-foreground">{lf.field_label}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <Badge variant="outline" className={`text-[10px] ${typeBadge.color}`}>
                                {typeBadge.label}
                              </Badge>
                              {(lf.file_url || lf.storage_path) && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-7 text-xs gap-1" 
                                  onClick={() => openDocument(lf)} 
                                  data-testid={`lead-doc-open-${idx}`}
                                >
                                  {isPdf ? <Eye className="w-3 h-3" /> : <Download className="w-3 h-3" />}
                                  {isPdf ? "View" : "Download"}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Requested for Intake (existing system) */}
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                  {(submission.lead_files || []).length > 0 ? "Requested for Intake" : "Documents"}
                </h3>
                
                {requiredDocuments.length > 0 ? (
                  <div className="space-y-1.5 mb-3">
                    {requiredDocuments.map((doc, idx) => {
                      const filename = doc.file_name || doc.label || "";
                      const typeBadge = doc.status === "uploaded" && filename ? getFileTypeBadge(filename) : null;
                      const isPdf = filename ? isPDF(filename) : false;
                      return (
                        <div key={doc.doc_id || idx} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-md text-sm">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="font-medium truncate">{doc.label}</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {typeBadge && (
                              <Badge variant="outline" className={`text-[10px] ${typeBadge.color}`}>
                                {typeBadge.label}
                              </Badge>
                            )}
                            {doc.status === "uploaded" && (doc.url || doc.file_path) && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 text-xs gap-1" 
                                onClick={() => openDocument({ file_url: doc.url, storage_path: doc.file_path, filename: doc.file_name || doc.label })} 
                                data-testid={`doc-open-${idx}`}
                              >
                                {isPdf ? <Eye className="w-3 h-3" /> : <Download className="w-3 h-3" />}
                                {isPdf ? "View" : "Download"}
                              </Button>
                            )}
                            <Badge variant="outline" className={`text-xs ${doc.status === "uploaded" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600"}`}>
                              {doc.status === "uploaded" ? "Uploaded" : "Missing"}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mb-3">No documents required yet</p>
                )}

                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => onRequestDocuments?.(submission)} data-testid="request-documents-btn">
                    <FileUp className="w-3 h-3 mr-1.5" />
                    Request Documents
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => window.open(getClientPortalLink(), "_blank")} data-testid="open-portal-btn">
                    <ExternalLink className="w-3 h-3 mr-1.5" />
                    Open Client Portal
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => copyToClipboard(getClientPortalLink(), "portal")} data-testid="copy-portal-link-btn">
                    {linkCopied === "portal" ? <Check className="w-3 h-3 mr-1.5" /> : <Copy className="w-3 h-3 mr-1.5" />}
                    Copy Portal Link
                  </Button>
                </div>
              </div>

              {/* ============================================================= */}
              {/* E-SIGNATURE - Clean section */}
              {/* ============================================================= */}
              <div data-testid="esignature-section">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">E-Signature</h3>
                <div className="bg-slate-50 rounded-md p-3">
                  {submission.signwell_status === "signed" ? (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Document signed on {formatDate(submission.signwell_signed_at)}</span>
                    </div>
                  ) : submission.signwell_status === "pending" ? (
                    <div className="flex items-center gap-2 text-sm text-amber-600">
                      <Clock className="w-4 h-4" />
                      <span>Awaiting signature</span>
                    </div>
                  ) : submission.esign_document_name ? (
                    // Document uploaded but not sent yet
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200">
                        <FileText className="w-4 h-4 text-violet-600" />
                        <span className="text-sm font-medium flex-1 truncate">{submission.esign_document_name}</span>
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Uploaded</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Ready to send for signature</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEsignUploadOpen(true)} data-testid="upload-doc-btn">
                            <Upload className="w-3 h-3 mr-1.5" />
                            Replace
                          </Button>
                          <Button 
                            size="sm" 
                            className="h-8 text-xs bg-violet-600 hover:bg-violet-700" 
                            onClick={() => onSendForSignature?.(submission)}
                            data-testid="send-for-signature-btn"
                          >
                            <PenLine className="w-3 h-3 mr-1.5" />
                            Send for Signature
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">No documents sent for signature</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEsignUploadOpen(true)} data-testid="upload-doc-btn">
                          <Upload className="w-3 h-3 mr-1.5" />
                          Upload Document
                        </Button>
                        <Button 
                          size="sm" 
                          className="h-8 text-xs bg-violet-600 hover:bg-violet-700" 
                          disabled={true}
                          data-testid="send-for-signature-btn"
                        >
                          <PenLine className="w-3 h-3 mr-1.5" />
                          Send for Signature
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ============================================================= */}
              {/* FORMS & RESPONSES - Collapsible per form with AI Summary */}
              {/* ============================================================= */}
              <div data-testid="forms-responses-section">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Forms & Responses</h3>
                <div className="space-y-2">
                  {/* Lead Form Responses */}
                  {leadFormResponses.map((form, idx) => (
                    <Collapsible 
                      key={`lead-${idx}`} 
                      open={formsExpanded[`lead-${idx}`]} 
                      onOpenChange={(open) => setFormsExpanded(prev => ({ ...prev, [`lead-${idx}`]: open }))}
                    >
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 bg-slate-50 rounded-md text-sm hover:bg-slate-100 transition-colors" data-testid={`form-lead-${idx}`}>
                        <div className="flex items-center gap-2">
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${formsExpanded[`lead-${idx}`] ? "rotate-90" : ""}`} />
                          <span className="font-medium">{form.form_name || "Lead Form"}</span>
                          <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">Completed</Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mt-1 ml-6 pl-3 border-l-2 border-slate-200 space-y-2 py-2">
                          {/* AI Summary & Score Section */}
                          {(submission.summary || submission.ai_summary || submission.lead_ai_summary || aiScore) && (
                            <div className="bg-violet-50 border border-violet-200 rounded-md p-2.5 mb-2">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                                <span className="text-xs font-medium text-violet-700">
                                  {submission.ai_analysis_used === false ? "Rule-Based Score" : "AI Analysis"}
                                </span>
                                {aiScore && (
                                  <Badge className="ml-auto text-[10px] bg-violet-100 text-violet-700 border-violet-200">
                                    Score: {aiScore}
                                  </Badge>
                                )}
                                {priorityLabel && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {priorityLabel}
                                  </Badge>
                                )}
                              </div>
                              {/* AI skip reason */}
                              {submission.ai_analysis_used === false && submission.ai_skip_reason && (
                                <p className="text-[10px] text-amber-600 mb-1.5">
                                  {submission.ai_skip_reason === "insufficient_text" && "AI skipped: not enough text content to analyze"}
                                  {submission.ai_skip_reason === "no_valid_fields" && "AI skipped: no scoreable fields configured"}
                                  {submission.ai_skip_reason === "ai_error" && "AI skipped: analysis encountered an error"}
                                  {submission.ai_skip_reason === "ai_disabled" && "AI skipped: scoring temporarily disabled"}
                                  {submission.ai_skip_reason === "daily_limit_exceeded" && "AI skipped: daily limit reached"}
                                </p>
                              )}
                              {(submission.summary || submission.ai_summary || submission.lead_ai_summary) && (
                                <p className="text-xs text-slate-600 leading-relaxed">
                                  {submission.summary || submission.ai_summary || submission.lead_ai_summary}
                                </p>
                              )}
                              {/* Score Breakdown Per Field */}
                              {submission.score_breakdown_detailed && Object.keys(submission.score_breakdown_detailed).length > 0 && (
                                <div className="mt-2 pt-2 border-t border-violet-200/60 space-y-1">
                                  <p className="text-[10px] font-medium text-violet-600 uppercase tracking-wider">Breakdown</p>
                                  {Object.entries(submission.score_breakdown_detailed).map(([field, data]) => {
                                    const score = typeof data === "object" ? data.score : data;
                                    const max = typeof data === "object" ? data.max : null;
                                    const pct = max ? Math.round((score / max) * 100) : null;
                                    return (
                                      <div key={field} className="flex items-center gap-2 text-[11px]">
                                        <span className="text-slate-500 min-w-[100px] truncate">{field}</span>
                                        <div className="flex-1 h-1.5 bg-violet-100 rounded-full overflow-hidden">
                                          <div className="h-full rounded-full" style={{ width: `${pct || 50}%`, backgroundColor: pct > 60 ? "#7c3aed" : pct > 30 ? "#a78bfa" : "#ddd6fe" }} />
                                        </div>
                                        <span className="text-violet-700 font-medium tabular-nums">{score}{max ? `/${max}` : ""}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {/* Fallback: show simple breakdown if no detailed version */}
                              {!submission.score_breakdown_detailed && submission.score_breakdown && Object.keys(submission.score_breakdown).length > 0 && (
                                <div className="mt-2 pt-2 border-t border-violet-200/60 space-y-1">
                                  <p className="text-[10px] font-medium text-violet-600 uppercase tracking-wider">Breakdown</p>
                                  {Object.entries(submission.score_breakdown).map(([field, score]) => (
                                    <div key={field} className="flex items-center justify-between text-[11px]">
                                      <span className="text-slate-500 truncate">{field}</span>
                                      <span className="text-violet-700 font-medium">{score}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Form Field Responses */}
                          {form.data && typeof form.data === 'object' && Object.entries(form.data).filter(([k]) => !k.startsWith("_")).map(([key, value]) => (
                            <div key={key} className="flex text-sm">
                              <span className="text-muted-foreground min-w-[120px]">{key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                              <span className="flex-1">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                  
                  {/* Intake Form Responses */}
                  {intakeFormResponses.map((form, idx) => (
                    <Collapsible 
                      key={`intake-${idx}`} 
                      open={formsExpanded[`intake-${idx}`]} 
                      onOpenChange={(open) => setFormsExpanded(prev => ({ ...prev, [`intake-${idx}`]: open }))}
                    >
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 bg-slate-50 rounded-md text-sm hover:bg-slate-100 transition-colors" data-testid={`form-intake-${idx}`}>
                        <div className="flex items-center gap-2">
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${formsExpanded[`intake-${idx}`] ? "rotate-90" : ""}`} />
                          <span className="font-medium">{form.form_name || "Intake Form"}</span>
                          <Badge variant="outline" className={`text-[10px] ${form.submitted_at ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            {form.submitted_at ? "Completed" : "In Progress"}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mt-1 ml-6 pl-3 border-l-2 border-slate-200 space-y-2 py-2">
                          {/* Intake AI Summary Section - show ALL intake_ai fields */}
                          {submission.intake_ai && (
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 mb-2 space-y-2">
                              <div className="flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                                <span className="text-xs font-medium text-blue-700">Intake AI Analysis</span>
                              </div>
                              {/* Request Overview */}
                              {(submission.intake_ai.summary?.request_overview || submission.intake_ai_summary) && (
                                <p className="text-xs text-slate-600 leading-relaxed">
                                  {submission.intake_ai.summary?.request_overview || submission.intake_ai_summary}
                                </p>
                              )}
                              {/* Missing Fields */}
                              {submission.intake_ai.missing_fields?.length > 0 && (
                                <div className="text-xs">
                                  <span className="font-medium text-amber-700">Missing fields: </span>
                                  <span className="text-slate-600">{submission.intake_ai.missing_fields.join(", ")}</span>
                                </div>
                              )}
                              {/* Risk Flags */}
                              {submission.intake_ai.risk_flags?.length > 0 && (
                                <div className="text-xs">
                                  <span className="font-medium text-red-700">Risk flags: </span>
                                  <span className="text-slate-600">{submission.intake_ai.risk_flags.join(", ")}</span>
                                </div>
                              )}
                              {/* Timeline */}
                              {submission.intake_ai.summary?.timeline_context && (
                                <div className="text-xs">
                                  <span className="font-medium text-blue-700">Timeline: </span>
                                  <span className="text-slate-600">{submission.intake_ai.summary.timeline_context}</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Form Field Responses - show ALL submitted fields */}
                          {form.data && typeof form.data === 'object' && Object.entries(form.data).filter(([k]) => !k.startsWith("_")).map(([key, value]) => (
                            <div key={key} className="flex text-sm">
                              <span className="text-muted-foreground min-w-[120px]">{key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                              <span className="flex-1 break-words">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                            </div>
                          ))}
                          {(!form.data || Object.keys(form.data).length === 0) && (
                            <p className="text-sm text-muted-foreground italic">No data yet</p>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}

                  {leadFormResponses.length === 0 && intakeFormResponses.length === 0 && (
                    <p className="text-sm text-muted-foreground py-2">No form responses yet</p>
                  )}
                </div>
              </div>
            </div>
          </ScrollArea>

          {/* ================================================================= */}
          {/* RIGHT SIDEBAR - Activity + Notes + Tags - SINGLE CONTINUOUS COLUMN */}
          {/* ================================================================= */}
          <div 
            className="w-72 flex-shrink-0 flex flex-col bg-slate-50/50 border-l" 
            style={{ minHeight: 0 }}
            data-testid="right-sidebar"
          >
            {/* Tab Headers - Custom implementation to avoid Radix margin issues */}
            <div className="flex bg-white border-b flex-shrink-0">
              <button
                onClick={() => setActiveRightTab("activity")}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
                  activeRightTab === "activity" 
                    ? "bg-slate-50 text-slate-900 border-b-2 border-slate-900" 
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <History className="w-3.5 h-3.5" />
                Activity
              </button>
              <button
                onClick={() => setActiveRightTab("notes")}
                className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
                  activeRightTab === "notes" 
                    ? "bg-slate-50 text-slate-900 border-b-2 border-slate-900" 
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <StickyNote className="w-3.5 h-3.5" />
                Notes
              </button>
            </div>
            
            {/* Content Area - SINGLE flex container that fills ALL available space */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              
              {/* ACTIVITY TAB CONTENT */}
              {activeRightTab === "activity" && (
                <div className="flex-1 overflow-y-auto min-h-0" data-testid="activity-list">
                  <div className="p-3 space-y-3">
                    {(() => {
                      // ============================================
                      // BUILD COMPLETE ACTIVITY TIMELINE
                      // ============================================
                      
                      // 1. Collect ALL activity sources
                      const allActivities = [];
                      
                      // Source 1: activities array (newer format)
                      (submission.activities || []).forEach(a => {
                        allActivities.push({ 
                          ...a, 
                          timestamp: a.timestamp || a.created_at || a.at,
                          _ts: new Date(a.timestamp || a.created_at || a.at).getTime(),
                          _source: 'activities'
                        });
                      });
                      
                      // Source 2: activity_log array (older format)
                      (submission.activity_log || []).forEach(a => {
                        allActivities.push({ 
                          ...a, 
                          timestamp: a.at || a.timestamp,
                          _ts: new Date(a.at || a.timestamp).getTime(),
                          _source: 'activity_log'
                        });
                      });
                      
                      // Source 3: Add submission creation as first event if not present
                      const hasCreationEvent = allActivities.some(a => 
                        ['created', 'lead_created', 'submission_created', 'created_from_contact'].includes(a.action)
                      );
                      if (!hasCreationEvent && submission.created_at) {
                        allActivities.push({
                          action: 'submission_created',
                          timestamp: submission.created_at,
                          _ts: new Date(submission.created_at).getTime(),
                          _source: 'system'
                        });
                      }
                      
                      // 2. FILTER: Remove invalid/placeholder events
                      const invalidActions = new Set(['sent', 'activity', 'Activity', 'undefined', '', 'null']);
                      const validActivities = allActivities.filter(a => {
                        const action = (a.action || '').trim().toLowerCase();
                        if (!action || invalidActions.has(action)) return false;
                        return true;
                      });
                      
                      // 3. DEDUPLICATE: Remove exact duplicates (same action + timestamp within 1 second)
                      const seenKeys = new Set();
                      const deduped = validActivities.filter(a => {
                        const key = `${a.action}-${Math.floor(a._ts / 1000)}`;
                        if (seenKeys.has(key)) return false;
                        seenKeys.add(key);
                        return true;
                      });
                      
                      // 4. SORT: Newest first (descending)
                      const sorted = deduped.sort((a, b) => b._ts - a._ts);
                      
                      // 5. Empty state
                      if (sorted.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <CircleDot className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-xs text-muted-foreground">No activity yet</p>
                          </div>
                        );
                      }
                      
                      // 6. RENDER: Map each activity to clean label + icon
                      return sorted.map((activity, idx) => {
                        const action = (activity.action || '').toLowerCase();
                        const meta = activity.meta || activity.details || {};
                        
                        // Determine description and icon
                        let desc = "";
                        let icon = <CircleDot className="w-3.5 h-3.5" />;
                        let iconBg = "bg-slate-100";
                        
                        // ---- CREATION EVENTS ----
                        if (action === 'submission_created' || action === 'lead_created' || action === 'created') {
                          desc = "Submission created";
                          icon = <Plus className="w-3.5 h-3.5" />;
                          iconBg = "bg-blue-100";
                        } 
                        else if (action === 'created_from_contact' || action === 'contact_created') {
                          desc = "Created from contact";
                          icon = <Plus className="w-3.5 h-3.5" />;
                          iconBg = "bg-blue-100";
                        }
                        else if (action === 'created_manually_from_contact' || action === 'created manually from contact') {
                          desc = "Created manually from contact";
                          icon = <Plus className="w-3.5 h-3.5" />;
                          iconBg = "bg-blue-100";
                        }
                        
                        // ---- FORM EVENTS ----
                        else if (action === 'lead_form_submitted' || action === 'form_submitted') {
                          desc = "Lead form submitted by client";
                          icon = <ClipboardList className="w-3.5 h-3.5" />;
                          iconBg = "bg-green-100";
                        }
                        else if (action === 'intake_form_submitted') {
                          desc = "Intake form submitted by client";
                          icon = <ClipboardList className="w-3.5 h-3.5" />;
                          iconBg = "bg-green-100";
                        }
                        else if (action === 'intake_form_sent' || action === 'form_sent') {
                          const formName = meta.form_name || meta.formName;
                          desc = formName ? `Form '${formName}' sent to client` : "Intake form sent to client";
                          icon = <Send className="w-3.5 h-3.5" />;
                          iconBg = "bg-violet-100";
                        }
                        
                        // ---- EMAIL EVENTS ----
                        else if (action === 'email_sent') {
                          const emailType = meta.template || meta.type;
                          desc = emailType ? `Email sent: ${emailType}` : "Email sent to client";
                          icon = <Mail className="w-3.5 h-3.5" />;
                          iconBg = "bg-violet-100";
                        }
                        else if (action === 'email_delivered') {
                          desc = "Email delivered";
                          icon = <CheckCircle2 className="w-3.5 h-3.5" />;
                          iconBg = "bg-green-100";
                        }
                        else if (action === 'email_bounced' || action === 'email_failed') {
                          desc = "Email bounced/failed";
                          icon = <AlertCircle className="w-3.5 h-3.5" />;
                          iconBg = "bg-red-100";
                        }
                        else if (action === 'intake_email_sent') {
                          desc = "Intake email sent to client";
                          icon = <Mail className="w-3.5 h-3.5" />;
                          iconBg = "bg-violet-100";
                        }
                        
                        // ---- DOCUMENT EVENTS ----
                        else if (action === 'documents_requested' || action === 'document_requested') {
                          desc = "Documents requested from client";
                          icon = <FileUp className="w-3.5 h-3.5" />;
                          iconBg = "bg-orange-100";
                        }
                        else if (action === 'document_uploaded') {
                          const docName = meta.document_name || meta.filename;
                          desc = docName ? `Document uploaded: ${docName}` : "Document uploaded by client";
                          icon = <FileText className="w-3.5 h-3.5" />;
                          iconBg = "bg-green-100";
                        }
                        
                        // ---- SIGNATURE EVENTS ----
                        else if (action === 'esign_document_uploaded' || action === 'contract_uploaded') {
                          desc = "Contract uploaded for signature";
                          icon = <PenLine className="w-3.5 h-3.5" />;
                          iconBg = "bg-violet-100";
                        }
                        else if (action === 'signature_requested' || action === 'contract_sent') {
                          desc = "Sent for e-signature";
                          icon = <PenLine className="w-3.5 h-3.5" />;
                          iconBg = "bg-violet-100";
                        }
                        else if (action === 'document_signed') {
                          desc = "Document signed by client";
                          icon = <CheckCircle2 className="w-3.5 h-3.5" />;
                          iconBg = "bg-green-100";
                        }
                        
                        // ---- STATUS CHANGE EVENTS ----
                        else if (action === 'lead_status_changed' || action === 'status_changed') {
                          const from = meta.from || '';
                          const to = meta.to || '';
                          if (to) {
                            desc = `Lead status → ${to.replace(/_/g, " ")}`;
                          } else {
                            desc = "Lead status changed";
                          }
                          icon = <ArrowRight className="w-3.5 h-3.5" />;
                          iconBg = "bg-blue-100";
                        }
                        else if (action === 'intake_stage_changed') {
                          const to = meta.to || '';
                          if (to) {
                            desc = `Intake stage → ${to.replace(/_/g, " ")}`;
                          } else {
                            desc = "Intake stage changed";
                          }
                          icon = <ArrowRight className="w-3.5 h-3.5" />;
                          iconBg = "bg-purple-100";
                        }
                        else if (action === 'service_stage_changed') {
                          const to = meta.to || '';
                          if (to) {
                            desc = `Service stage → ${to.replace(/_/g, " ")}`;
                          } else {
                            desc = "Service stage changed";
                          }
                          icon = <ArrowRight className="w-3.5 h-3.5" />;
                          iconBg = "bg-teal-100";
                        }
                        
                        // ---- WORKFLOW EVENTS ----
                        else if (action === 'intake_workflow_started' || action === 'workflow_started') {
                          desc = "Intake workflow started";
                          icon = <Play className="w-3.5 h-3.5" />;
                          iconBg = "bg-green-100";
                        }
                        else if (action === 'cascade_reset' || action === 'workflow_reset') {
                          const stagesReset = meta.stages_reset || meta.reset_stages || [];
                          if (stagesReset.length > 0) {
                            const names = stagesReset.map(s => s.replace('_stage', '')).join(', ');
                            desc = `Workflow reset: ${names} stage(s) cleared`;
                          } else {
                            desc = "Workflow stages reset";
                          }
                          icon = <RotateCcw className="w-3.5 h-3.5" />;
                          iconBg = "bg-amber-100";
                        }
                        
                        // ---- FALLBACK ----
                        else {
                          // Try to use description field if available
                          if (activity.description) {
                            desc = activity.description;
                          } else {
                            // Clean up action name
                            desc = action.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                          }
                        }
                        
                        return (
                          <div key={`${action}-${idx}`} className="flex items-start gap-2.5" data-testid={`activity-item-${idx}`}>
                            <div className={`w-6 h-6 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0 mt-0.5 text-slate-600`}>
                              {icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-700 leading-snug">{desc}</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">{formatDateTime(activity.timestamp)}</p>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
              
              {/* NOTES TAB CONTENT */}
              {activeRightTab === "notes" && (
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <div className="flex-1 overflow-y-auto min-h-0">
                    <div className="p-3 space-y-2">
                      {(submission.notes || []).map((note, idx) => (
                        <div key={idx} className="p-2 bg-white rounded border text-xs" data-testid={`note-item-${idx}`}>
                          <p className="text-slate-700">{note.content}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{formatDateTime(note.created_at)}</p>
                        </div>
                      ))}
                      {(!submission.notes || submission.notes.length === 0) && (
                        <div className="text-center py-8">
                          <StickyNote className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-xs text-muted-foreground">No notes yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Quick note input */}
                  <div className="p-3 border-t bg-white flex-shrink-0">
                    <div className="relative">
                      <Textarea 
                        value={newNote} 
                        onChange={(e) => setNewNote(e.target.value)} 
                        placeholder="Add a note..." 
                        className="text-xs min-h-[50px] resize-none pr-8"
                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleAddNote())}
                        data-testid="quick-note-input"
                      />
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="absolute bottom-1 right-1 h-6 w-6" 
                        onClick={handleAddNote} 
                        disabled={addingNote || !newNote.trim()}
                        data-testid="submit-note-btn"
                      >
                        {addingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* TAGS SECTION - Pinned at bottom */}
            <div className="border-t bg-white p-3 flex-shrink-0" data-testid="tags-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                  <h4 className="text-xs font-medium text-muted-foreground">Tags</h4>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0 hover:bg-slate-100" 
                  onClick={() => setTagInputOpen(!tagInputOpen)}
                  data-testid="add-tag-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              
              {/* Tag input */}
              {tagInputOpen && (
                <div className="flex gap-1.5 mb-2">
                  <Input 
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                    placeholder="Enter tag..."
                    className="h-7 text-xs flex-1"
                    autoFocus
                    data-testid="tag-input"
                  />
                  <Button 
                    size="sm" 
                    className="h-7 px-2.5 text-xs"
                    onClick={handleAddTag}
                    disabled={savingTag || !newTag.trim()}
                    data-testid="save-tag-btn"
                  >
                    {savingTag ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
                  </Button>
                </div>
              )}
              
              <div className="flex flex-wrap gap-1.5">
                {(submission.tags || []).map((tag, idx) => (
                  <Badge 
                    key={idx} 
                    variant="secondary" 
                    className="text-[10px] px-2 py-0.5 pr-1 flex items-center gap-1 group hover:bg-slate-200"
                    data-testid={`tag-${idx}`}
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:bg-slate-300 rounded-full p-0.5 opacity-60 hover:opacity-100 transition-opacity"
                      data-testid={`remove-tag-${idx}`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </Badge>
                ))}
                {(!submission.tags || submission.tags.length === 0) && !tagInputOpen && (
                  <span className="text-[10px] text-muted-foreground">No tags</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================= */}
        {/* MODALS */}
        {/* ================================================================= */}
        
        {/* Resend Email Modal */}
        <Dialog open={resendModalOpen} onOpenChange={setResendModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Resend Email</DialogTitle>
              <DialogDescription>Update email address and resend.</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="space-y-1.5">
                <Label>Email Address</Label>
                <Input value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="client@example.com" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResendModalOpen(false)}>Cancel</Button>
              <Button onClick={handleResendEmail} disabled={resendingEmail || !resendEmail}>
                {resendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark as Won Modal */}
        <Dialog open={wonModalOpen} onOpenChange={setWonModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-green-500" />
                Mark as Won
              </DialogTitle>
              <DialogDescription>Start the intake workflow for this lead.</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox id="send-intake" checked={wonModalData.sendIntakeEmail} onCheckedChange={(c) => setWonModalData(p => ({ ...p, sendIntakeEmail: c }))} />
                <label htmlFor="send-intake" className="text-sm cursor-pointer">Send intake email to client</label>
              </div>
              {wonModalData.sendIntakeEmail && intakeForms?.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Select Intake Form</Label>
                  <Select value={wonModalData.intakeFormId || ""} onValueChange={(v) => setWonModalData(p => ({ ...p, intakeFormId: v }))}>
                    <SelectTrigger><SelectValue placeholder="Choose form" /></SelectTrigger>
                    <SelectContent>
                      {intakeForms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWonModalOpen(false)}>Cancel</Button>
              <Button onClick={handleConfirmWon} disabled={wonModalLoading} className="bg-green-600 hover:bg-green-700">
                {wonModalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
                Mark as Won
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Document Modal */}
        <Dialog open={addDocModalOpen} onOpenChange={setAddDocModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Document Requirement</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="space-y-1.5">
                <Label>Document Name</Label>
                <Input value={newDocLabel} onChange={(e) => setNewDocLabel(e.target.value)} placeholder="e.g., Signed Contract" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="doc-required" checked={newDocRequired} onCheckedChange={setNewDocRequired} />
                <label htmlFor="doc-required" className="text-sm cursor-pointer">Required</label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDocModalOpen(false)}>Cancel</Button>
              <Button onClick={handleAddDocument} disabled={!newDocLabel.trim() || savingDoc}>
                {savingDoc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* E-Signature Upload Modal */}
        <Dialog open={esignUploadOpen} onOpenChange={setEsignUploadOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Document for E-Signature</DialogTitle>
              <DialogDescription>
                Upload a document to send to {getName()} for electronic signature.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-violet-300 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setEsignFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="esign-file-input"
                />
                <label htmlFor="esign-file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  {esignFile ? (
                    <div>
                      <p className="text-sm font-medium text-slate-700">{esignFile.name}</p>
                      <p className="text-xs text-muted-foreground">{(esignFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-slate-700">Click to upload</p>
                      <p className="text-xs text-muted-foreground">PDF, DOC, or DOCX (max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
              {esignFile && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => setEsignFile(null)}>
                  <X className="w-3 h-3 mr-1" />
                  Remove file
                </Button>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEsignUploadOpen(false); setEsignFile(null); }}>Cancel</Button>
              <Button 
                onClick={async () => {
                  if (!esignFile) return;
                  setUploadingEsign(true);
                  try {
                    // Upload file to backend
                    const formData = new FormData();
                    formData.append("file", esignFile);
                    formData.append("submission_id", submission.id);
                    const response = await axios.post(`${API}/submissions/${submission.id}/esign-upload`, formData, {
                      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                    });
                    toast.success("Document uploaded for e-signature");
                    setEsignUploadOpen(false);
                    setEsignFile(null);
                    onRefresh?.();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to upload document");
                  } finally {
                    setUploadingEsign(false);
                  }
                }} 
                disabled={!esignFile || uploadingEsign}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {uploadingEsign ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload & Continue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Compose Email Modal */}
        <Dialog open={composeEmailOpen} onOpenChange={setComposeEmailOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Compose Email</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="space-y-1.5">
                <Label>To</Label>
                <Input value={getEmail()} readOnly className="bg-slate-50" />
              </div>
              <div className="space-y-1.5">
                <Label>Subject</Label>
                <Input placeholder="Email subject" />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea placeholder="Write your message..." rows={5} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setComposeEmailOpen(false)}>Cancel</Button>
              <Button onClick={() => { toast.success("Email sent"); setComposeEmailOpen(false); }}>
                <Send className="w-4 h-4 mr-2" />
                Send Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template Select Modal */}
        <Dialog open={templateSelectOpen} onOpenChange={setTemplateSelectOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Select Email Template</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-2 max-h-64 overflow-y-auto">
              {/* Flatten pre_booking and post_booking templates into one list */}
              {(() => {
                const allTemplates = [
                  ...(emailTemplates?.pre_booking || []),
                  ...(emailTemplates?.post_booking || []),
                  ...(Array.isArray(emailTemplates) ? emailTemplates : [])
                ];
                return allTemplates.length > 0 ? (
                  allTemplates.map(t => (
                    <Button key={t.id || t.template_id} variant="outline" className="w-full justify-start text-left h-auto py-3" onClick={() => { onSendEmail?.(submission, t.id || t.template_id); setTemplateSelectOpen(false); }}>
                      <div>
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.subject}</p>
                      </div>
                    </Button>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No templates available</p>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>

        {/* Backward Movement Confirmation Dialog */}
        <Dialog open={showBackwardConfirm} onOpenChange={setShowBackwardConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Confirm Workflow Change
              </DialogTitle>
              <DialogDescription className="pt-2">
                Moving this submission backward will reset progress in later stages.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {pendingTransition && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                  <p className="font-medium text-amber-800 mb-2">The following will be reset:</p>
                  <ul className="list-disc list-inside text-amber-700 space-y-1">
                    {pendingTransition.targetPhase === "lead" && (
                      <>
                        <li>Intake stage progress</li>
                        <li>Service stage progress</li>
                      </>
                    )}
                    {pendingTransition.targetPhase === "intake" && pendingTransition.currentPhase === "service" && (
                      <li>Service stage progress</li>
                    )}
                    {pendingTransition.currentPhase === null && (
                      <li>Reactivating from Lost status</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={cancelBackwardTransition}>
                Cancel
              </Button>
              <Button 
                variant="default" 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={confirmBackwardTransition}
              >
                Confirm & Reset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Won → Intake Transition Modal */}
        <Dialog open={showWonIntakeModal} onOpenChange={(open) => !wonIntakeLoading && setShowWonIntakeModal(open)}>
          <DialogContent className="max-w-[480px] p-6">
            <DialogHeader className="pb-2">
              <DialogTitle className="text-base font-semibold">Start Intake Process?</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                This lead has been marked as Won. Choose how you'd like to proceed:
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-3 space-y-4 text-sm">
              {/* Option 1: Start Intake & Send Form */}
              <div>
                <p className="font-medium text-slate-900">Start Intake & Send Form</p>
                <ul className="mt-1 text-xs text-slate-500 space-y-0.5 ml-4">
                  <li>• Move workflow to Intake phase</li>
                  <li>• Set intake status to "Requested"</li>
                  <li>• Send intake form email</li>
                </ul>
              </div>
              
              {/* Option 2: Start Intake Only */}
              <div>
                <p className="font-medium text-slate-900">Start Intake Only</p>
                <ul className="mt-1 text-xs text-slate-500 space-y-0.5 ml-4">
                  <li>• Move workflow to Intake phase</li>
                  <li>• Set intake status to "Requested"</li>
                  <li>• No email sent</li>
                </ul>
              </div>
              
              {/* Option 3: Skip */}
              <div>
                <p className="font-medium text-slate-900">Skip for now</p>
                <ul className="mt-1 text-xs text-slate-500 space-y-0.5 ml-4">
                  <li>• Keep lead in Lead phase as Won</li>
                </ul>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 pt-4 border-t">
              <Button 
                onClick={handleWonStartIntakeWithEmail}
                disabled={wonIntakeLoading}
                className="w-full"
              >
                {wonIntakeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Start Intake & Send Form
              </Button>
              <div className="flex justify-between">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => !wonIntakeLoading && setShowWonIntakeModal(false)}
                  disabled={wonIntakeLoading}
                >
                  Cancel
                </Button>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={handleWonSkipIntake}
                    disabled={wonIntakeLoading}
                  >
                    Skip
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handleWonStartIntakeOnly}
                    disabled={wonIntakeLoading}
                  >
                    Start Intake Only
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </SheetContent>
    </Sheet>
  );
}
